import React, { useEffect, useRef, useState } from 'react';
import { View, Text, Image, StyleSheet, Alert, Pressable, ActivityIndicator, Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import { colors, fonts, radii, spacing } from '../theme';
import { Button } from '../shared/Button';
import * as ImagePicker from 'expo-image-picker';
import { manipulateAsync, SaveFormat } from 'expo-image-manipulator';
import * as FileSystem from 'expo-file-system';
import { v7 as uuidv7 } from 'uuid';
import { supabase } from '../../lib/supabase';
import { recordUploadedPhoto, deleteProjectPhoto } from '../../features/projects/projectPhotos';
import { buildProjectPhotoStoragePath, PROJECT_PHOTOS_BUCKET } from '../../features/projects/photoStoragePath';
import { buildResizeAction, PHOTO_COMPRESSION_QUALITY } from '../../features/projects/photoCompression';
import { hasReachedPhotoLimit, FREE_PLAN_PHOTO_LIMIT } from '../../features/projects/photoLimits';
import { retryWithBackoff, RetryAbortedError } from '../../features/projects/retryWithBackoff';
import { decodeBase64ToBytes } from '../../features/projects/base64';
import type { AppDatabase } from '../../db/outbox';
import type { ProjectPhoto } from '../../db/schema';

interface Props {
  db: AppDatabase;
  userId: string;
  projectId: string;
  photos: ProjectPhoto[];
  onPhotoUploaded: () => void;
}

type PendingStatus = 'uploading' | 'error';

interface PendingPhoto {
  localId: string;
  localUri: string;
  status: PendingStatus;
}

/**
 * Seção "fotos" da tela de detalhe do projeto (Etapa 11 — specs S02/S05
 * §Fotos). EXCEÇÃO deliberada ao padrão de escrita do projeto (ver
 * src/features/projects/projectPhotos.ts): a foto tirada/escolhida aparece
 * na UI IMEDIATAMENTE (arquivo local comprimido, com badge "enviando"),
 * mantida só em `pending` (estado React, memória) — a linha em
 * `project_photos` só é gravada (e só então entra na outbox, via
 * `recordUploadedPhoto`/`insertRecord`) depois que o upload ao Supabase
 * Storage já terminou com sucesso. Isso significa que, se o app for morto ou
 * esta tela for desmontada no meio de um upload, o item "enviando" se perde
 * e a usuária precisa tirar a foto de novo — limitação aceita para o MVP
 * (documentada no brief da Etapa 11 e no relatório da task).
 */
export function ProjectPhotosSection({ db, userId, projectId, photos, onPhotoUploaded }: Props) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<PendingPhoto[]>([]);
  // Cache local (só desta sessão do app) de uri local -> id da foto já
  // gravada: permite mostrar a miniatura do arquivo comprimido sem precisar
  // baixar de volta do Storage logo depois do próprio upload (download sob
  // demanda de fotos de OUTRO aparelho é opcional/fora do escopo desta
  // task, ver brief).
  const [localUriByPhotoId, setLocalUriByPhotoId] = useState<Record<string, string>>({});

  // Cancela retries em andamento quando a tela desmonta, para não vazar
  // trabalho em segundo plano nem chamar setState depois que o componente
  // já saiu (ver retryWithBackoff.ts).
  const cancelledRef = useRef(false);
  useEffect(() => {
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  const totalPhotoCount = photos.length + pending.length;
  const limitReached = hasReachedPhotoLimit(totalPhotoCount);

  const uploadPendingPhoto = async (localId: string, localUri: string) => {
    const photoId = uuidv7();
    const storagePath = buildProjectPhotoStoragePath({ userId, projectId, photoId });

    try {
      await retryWithBackoff(
        async () => {
          let bytes: ArrayBuffer;
          if (Platform.OS === 'web') {
            const response = await fetch(localUri);
            bytes = await response.arrayBuffer();
          } else {
            const base64 = await FileSystem.readAsStringAsync(localUri, { encoding: FileSystem.EncodingType.Base64 });
            bytes = decodeBase64ToBytes(base64).buffer;
          }
          const { error } = await supabase.storage.from(PROJECT_PHOTOS_BUCKET).upload(storagePath, bytes, {
            contentType: 'image/jpeg',
            upsert: true,
          });
          if (error) {
            throw error;
          }
        },
        { shouldAbort: () => cancelledRef.current }
      );

      if (cancelledRef.current) return;

      const saved = await recordUploadedPhoto(db, { projectId, storagePath });
      if (cancelledRef.current) return;

      setLocalUriByPhotoId((prev) => ({ ...prev, [saved.id]: localUri }));
      setPending((prev) => prev.filter((p) => p.localId !== localId));
      onPhotoUploaded();
    } catch (error) {
      if (error instanceof RetryAbortedError || cancelledRef.current) {
        return;
      }
      console.error('ProjectPhotosSection: falha ao enviar foto', error);
      setPending((prev) => prev.map((p) => (p.localId === localId ? { ...p, status: 'error' } : p)));
    }
  };

  const pickAndQueuePhoto = async (source: 'camera' | 'gallery') => {
    if (limitReached) {
      Alert.alert(t('project.photos.limitTitle'), t('project.photos.limitMessage', { limit: FREE_PLAN_PHOTO_LIMIT }));
      return;
    }

    if (Platform.OS !== 'web') {
      const permission =
        source === 'camera'
          ? await ImagePicker.requestCameraPermissionsAsync()
          : await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!permission.granted) {
        Alert.alert(t('project.photos.permissionDeniedTitle'), t('project.photos.permissionDeniedMessage'));
        return;
      }
    }

    const result =
      source === 'camera' ? await ImagePicker.launchCameraAsync({ quality: 1 }) : await ImagePicker.launchImageLibraryAsync({ quality: 1 });

    if (result.canceled || !result.assets?.[0]) {
      return;
    }

    const asset = result.assets[0];

    let manipulatedUri: string;
    try {
      const resizeAction = buildResizeAction(asset.width, asset.height);
      const manipulated = await manipulateAsync(asset.uri, resizeAction ? [resizeAction] : [], {
        compress: PHOTO_COMPRESSION_QUALITY,
        format: SaveFormat.JPEG,
      });
      manipulatedUri = manipulated.uri;
    } catch (error) {
      if (Platform.OS === 'web') {
        manipulatedUri = asset.uri;
      } else {
        console.error('ProjectPhotosSection: falha ao comprimir foto', error);
        Alert.alert(t('project.photos.compressionErrorTitle'), t('project.photos.compressionErrorMessage'));
        return;
      }
    }

    const localId = uuidv7();
    setPending((prev) => [...prev, { localId, localUri: manipulatedUri, status: 'uploading' }]);
    void uploadPendingPhoto(localId, manipulatedUri);
  };

  const handleDeletePhoto = (photoId: string) => {
    Alert.alert(t('project.photos.deleteConfirmTitle'), t('project.photos.deleteConfirmMessage'), [
      { text: t('project.photos.deleteConfirmCancel'), style: 'cancel' },
      {
        text: t('project.photos.deleteConfirmConfirm'),
        style: 'destructive',
        onPress: async () => {
          try {
            await deleteProjectPhoto(db, photoId);
            onPhotoUploaded();
          } catch (error) {
            Alert.alert(t('project.photos.deleteErrorTitle'), (error as Error).message);
          }
        },
      },
    ]);
  };

  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{t('project.photos.title')}</Text>

      {photos.length === 0 && pending.length === 0 ? <Text style={styles.muted}>{t('project.photos.empty')}</Text> : null}

      <View style={styles.grid}>
        {photos.map((photo) => {
          const localUri = localUriByPhotoId[photo.id];
          return (
            <Pressable key={photo.id} style={styles.thumbWrapper} onLongPress={() => handleDeletePhoto(photo.id)}>
              {localUri ? (
                <Image source={{ uri: localUri }} style={styles.thumb} />
              ) : (
                <View style={[styles.thumb, styles.thumbPlaceholder]}>
                  <Text style={styles.thumbPlaceholderText}>{t('project.photos.syncedPlaceholder')}</Text>
                </View>
              )}
            </Pressable>
          );
        })}

        {pending.map((item) => (
          <View key={item.localId} style={styles.thumbWrapper}>
            <Image source={{ uri: item.localUri }} style={styles.thumb} />
            <View style={styles.badge}>
              {item.status === 'uploading' ? (
                <>
                  <ActivityIndicator size="small" color="#fff" />
                  <Text style={styles.badgeText}>{t('project.photos.uploadingBadge')}</Text>
                </>
              ) : (
                <Text style={styles.badgeText}>{t('project.photos.errorBadge')}</Text>
              )}
            </View>
          </View>
        ))}
      </View>

      {limitReached && (
        <View style={styles.limitNotice}>
          <Text style={styles.limitNoticeText}>
            {t('project.photos.limitMessage', { limit: FREE_PLAN_PHOTO_LIMIT })}
          </Text>
        </View>
      )}

      <View style={styles.buttonRow}>
        <View style={styles.buttonWrapper}>
          <Button title={t('project.photos.cameraButton')} variant="line" onPress={() => pickAndQueuePhoto('camera')} disabled={limitReached} />
        </View>
        <View style={styles.buttonWrapper}>
          <Button title={t('project.photos.galleryButton')} variant="line" onPress={() => pickAndQueuePhoto('gallery')} disabled={limitReached} />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  section: { width: '100%', marginTop: spacing.md },
  sectionTitle: { fontFamily: fonts.display, fontSize: 16, color: colors.ink, marginBottom: spacing.sm },
  muted: { fontFamily: fonts.body, color: colors.thread, marginBottom: spacing.sm },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.sm },
  thumbWrapper: { width: 88, height: 88 },
  thumb: { width: 88, height: 88, borderRadius: radii.sm, backgroundColor: colors.sandPanel },
  thumbPlaceholder: { justifyContent: 'center', alignItems: 'center', padding: spacing.xs },
  thumbPlaceholderText: { fontFamily: fonts.body, fontSize: 10, color: colors.thread, textAlign: 'center' },
  badge: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(44,47,34,0.7)',
    paddingVertical: 2,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    borderBottomLeftRadius: radii.sm,
    borderBottomRightRadius: radii.sm,
  },
  badgeText: { fontFamily: fonts.body, color: colors.onSage, fontSize: 10, marginLeft: spacing.xs },
  limitNotice: { backgroundColor: colors.sandPanel, padding: spacing.sm, borderRadius: radii.sm, marginBottom: spacing.sm },
  limitNoticeText: { fontFamily: fonts.body, color: colors.clay, fontSize: 13 },
  buttonRow: { flexDirection: 'row', gap: spacing.sm },
  buttonWrapper: { flex: 1 },
});
